/**
 * DataTable helpers that are not selection maths — pure, and testable by
 * Node's runner, which resolves no bare imports from `shared/ui`.
 *
 * `withLeadingCells` needs React's element API at runtime, so it takes it as
 * a parameter (DataTable passes `React`, the test passes the tenant's copy);
 * this file imports React's TYPES only, which the type stripper erases.
 * Pinned by `tenant/frontend/hooks/dataTable.test.ts`.
 */
import type { ReactElement, ReactNode } from 'react';

/** The slice of React's runtime `withLeadingCells` needs. */
export type ReactElementApi = Pick<typeof import('react'), 'isValidElement' | 'cloneElement' | 'Children' | 'Fragment'>;

/**
 * Prepend `n` empty cells (`pad(i)`) to the `<tr>` a caller's
 * `renderFooterRow` returned — or to each `<tr>` of a fragment — so a totals
 * row stays aligned under the selection / chevron columns the caller does not
 * know about. Only a LITERAL `<tr>` can be padded: a component that renders
 * one is opaque here (returned untouched), which is why `renderFooterRow`
 * also receives the count to pad with itself.
 */
export function withLeadingCells(node: ReactNode, n: number, pad: (index: number) => ReactNode, R: ReactElementApi): ReactNode {
    if (n <= 0 || !R.isValidElement(node)) return node;
    const el = node as ReactElement<{ children?: ReactNode }>;
    if (el.type === 'tr') {
        const pads = Array.from({ length: n }, (_, i) => pad(i));
        return R.cloneElement(el, undefined, ...pads, ...R.Children.toArray(el.props.children));
    }
    if (el.type === R.Fragment) {
        return R.Children.map(el.props.children, child => withLeadingCells(child, n, pad, R));
    }
    return node;
}

/**
 * A row key made safe for an HTML `id` / an `aria-controls` IDREF. Keys come
 * from workshop data («Cliente García 01»): a space would split the IDREF
 * list into three ids that do not exist. Letters, digits and `-` stay; every
 * other character (`_` included, so the mapping stays one-to-one) becomes
 * `_<hex>_`.
 */
export function domIdPart(key: string | number): string {
    return String(key).replace(/[^A-Za-z0-9-]/g, ch => `_${ch.charCodeAt(0).toString(16)}_`);
}
